import { Link, useNavigate, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Button } from "../components/ui/button";
import SEO from "../components/SEO";
import { ChevronRight, ShieldCheck, MapPin, Search, Mail, CheckCircle2, Info, Wrench } from "lucide-react";

// Per-city long-form content (Feb 2026 SEO expansion).
// Each city has its own hook, common-sense angles, and FAQ — no invented
// shop counts, no fake coverage numbers. Copy is en-NZ, practical, and
// avoids positioning TyreMatch as a mobile van or the tyre seller.
const CITIES = {
  auckland: {
    name: "Auckland",
    slug: "auckland",
    metaDesc:
      "Get private tyre quotes from local Auckland shops. Shops can't see each other's prices — compare and book fitment near you.",
    heroLead:
      "Auckland has one of NZ's largest tyre-shop networks — great for comparing options quickly, especially when you'd rather not ring around. TyreMatch lets you request once and get private quotes from nearby shops so you can compare on price, brand, and fitment date without repeating your details.",
    body: [
      {
        h: "Why private multi-shop quotes work well in Auckland",
        p: "Tyre pricing in Auckland can vary a lot between shops on the same fitted brand — different overheads, different volume deals, and different stock on the day. Ringing three or four shops for the same tyre is time-consuming, and once a shop knows a competitor quoted you, price discussions can drift. On TyreMatch, each shop submits their quote privately. Rival shops can't see your other quotes, your contact details, or your notes to other shops. You keep control until you choose one.",
      },
      {
        h: "Coverage across Auckland — from North Shore to South Auckland",
        p: "TyreMatch matches your request to shops within the search radius you set from your address. That means whether you're on the North Shore, out West, in the central suburbs, or heading south towards Manukau, the quotes you receive will come from shops that can actually service your suburb. You choose the radius — start smaller if you'd prefer a shop closer to home or work, widen it if you'd like more options.",
      },
      {
        h: "Mobile vs in-shop fitment — you choose the shop",
        p: "Some Auckland customers prefer to drop the car at a workshop and grab a coffee; others would rather have a mobile fitter come to their driveway or the office car park. TyreMatch itself doesn't fit tyres — we're a marketplace connecting you with local shops. If mobile / at-home fitting matters to you, mention it in the request note and pick a shop that offers it. You pay the shop directly at fitment.",
      },
      {
        h: "WOF and pre-WOF tyre planning",
        p: "New Zealand's WOF tread minimum is 1.5 mm across the principal tread pattern, but wet-weather grip drops well before that — many drivers replace nearer 3 mm. If you're heading in for a WOF and want to sort tyres in the same window, request quotes a few days before your WOF appointment. Shops can factor your service date into their offer so you don't get caught out on the day.",
      },
      {
        h: "How it works, in three steps",
        p: "1) Enter your rego (or tyre size), pick services (tyres, alignment, rotation) and set your search radius. 2) Nearby Auckland shops send private quotes. Shops can't see each other's prices. 3) Compare on price, brand, ratings and fitment date, choose a shop, and book. Payment happens at the shop.",
      },
    ],
    faq: [
      {
        q: "How many quotes will I get for a tyre request in Auckland?",
        a: "It depends on how many shops within your search radius are actively using TyreMatch right now. Widening the radius usually returns more options.",
      },
      {
        q: "Do shops see each other's quotes?",
        a: "No. Every shop quotes privately. They don't see rival prices, your other notes, or your contact details until you choose them.",
      },
      {
        q: "Does TyreMatch sell the tyres?",
        a: "No. We're a marketplace. You pay the tyre shop directly for the tyres and fitment. TyreMatch is not the seller or the payment processor for fitment.",
      },
      {
        q: "Can I get mobile / at-home tyre fitting in Auckland?",
        a: "Yes — mention that in your request note. Some shops offer mobile fitting; the quotes you receive will make it clear. You pick the shop that suits you.",
      },
    ],
  },
  wellington: {
    name: "Wellington",
    slug: "wellington",
    metaDesc:
      "Compare private tyre quotes from local Wellington shops. Wet-weather grip matters here — request once and pick the shop that fits.",
    heroLead:
      "Wellington drivers deal with wet weather, coastal wind, and steep hill starts more often than most of the country. Tyre choice really matters here — and comparing local quotes shouldn't require ringing around every shop in Newtown, Petone and Porirua. TyreMatch lets you request once and receive private quotes from nearby shops.",
    body: [
      {
        h: "Wet-weather grip is a real thing in Wellington",
        p: "The greasy first rain after a dry spell, southerly downpours, and long motorway stretches near the harbour are all reasons Wellington drivers pay closer attention to tread depth. The legal WOF minimum is 1.5 mm, but grip on wet roads drops well before you get down there. Many shops recommend planning a replacement nearer 3 mm — especially for daily commutes over the Rimutakas or through the Ngauranga Gorge.",
      },
      {
        h: "Compare private quotes without national-brand upsell",
        p: "The Wellington 'buy tyres' search results skew heavily toward national retailer landing pages. That's fine if you already know exactly which SKU you want, but drivers who'd rather see what several local shops charge — including independents — often struggle to compare fairly. On TyreMatch, each shop quotes independently. You can weight your choice on price, brand you actually want, service date, or the shop's Google rating.",
      },
      {
        h: "Pre-WOF planning matters",
        p: "A Wellington WOF that fails on tread means an extra trip and often an unnecessary rush job. If your treads are getting close, request quotes a week before the WOF window — shops can build the service date into their quote so tyres are sorted before the check.",
      },
      {
        h: "Radius covers Wellington and nearby areas",
        p: "You choose the search radius from your address. That means Wellington City, Hutt Valley, and Porirua drivers can all use the same request — just widen the radius if you'd rather compare more shops. We don't publish separate suburb pages until shops are confirmed live in each pocket, so use the search radius to control who quotes.",
      },
      {
        h: "How it works, in three steps",
        p: "1) Enter your rego (or tyre size), pick services, set your radius. 2) Nearby Wellington shops send private quotes. 3) Compare and book with the shop you like. Payment happens at the shop.",
      },
    ],
    faq: [
      {
        q: "How much tread should I have going into a Wellington winter?",
        a: "The legal WOF minimum is 1.5 mm, but many drivers replace nearer 3 mm for reliable wet-weather grip. See our tread depth guide for how to check.",
      },
      {
        q: "Do I need dedicated winter tyres in Wellington?",
        a: "For most Wellington drivers, all-season tyres in good condition are fine. Dedicated winter tyres tend to make sense if you regularly drive to the mountains or on frost-prone rural roads.",
      },
      {
        q: "Can shops in Hutt Valley or Porirua quote on my request?",
        a: "Yes — widen your search radius and any active shops in Lower Hutt, Upper Hutt, Porirua or beyond can send a quote if you're within their service area.",
      },
      {
        q: "Do shops see each other's Wellington quotes?",
        a: "No. All quotes are private per shop. Your details stay hidden until you pick a shop.",
      },
    ],
  },
  christchurch: {
    name: "Christchurch",
    slug: "christchurch",
    metaDesc:
      "Get private tyre quotes from local Christchurch shops. Compare fairly across Riccarton, Papanui and beyond without ringing around.",
    heroLead:
      "Christchurch drivers cover a lot of ground — Canterbury Plains commutes, alpine day-trips, and winter frost weeks. Tyre pricing across the city varies more than you'd expect on the same fitted brand. TyreMatch lets you request quotes once and receive private offers from local shops so you can compare without spending your morning on the phone.",
    body: [
      {
        h: "Clear comparison across the city",
        p: "Google's Christchurch tyre results are dominated by national retailer landing pages. If you'd rather compare a couple of independents alongside the chains, TyreMatch surfaces every shop that quotes privately on your request — so you can weigh price, brand, service date, and the shop's Google rating side by side.",
      },
      {
        h: "Winter driving and tyre choice",
        p: "Frost and black-ice mornings on rural Canterbury roads are real. For most Christchurch drivers, quality all-season tyres in good condition are enough — but if you regularly drive to the mountains, dedicated winter tyres are worth considering. NZ's winter-tyre rule is a 4 mm minimum on the tread pattern for tyres marketed as winter tyres; that only applies when you're using true winter tyres, not all-seasons. If you're not sure what your car currently has, mention it in your request note and shops can advise.",
      },
      {
        h: "WOF tread fail — next steps",
        p: "If your WOF fails on tread depth (below the 1.5 mm legal minimum) you'll need replacement before re-check. Request quotes right away — many Christchurch shops can quote same-day and fit within a few days, and the quote email tells you the earliest fitment date. That's usually a lot faster than ringing five workshops.",
      },
      {
        h: "Coverage across Christchurch and beyond",
        p: "Set your radius from your address — Riccarton, Papanui, Halswell, Rangiora, or further out. Only shops that can actually service your suburb will quote, so distance shown next to each quote is real distance, not a headline number.",
      },
      {
        h: "How it works, in three steps",
        p: "1) Enter your rego (or tyre size), pick services and radius. 2) Local shops send private quotes — they can't see each other's prices. 3) Compare and book with the shop you choose. Payment is at the shop at fitment.",
      },
    ],
    faq: [
      {
        q: "What's the difference between the WOF tread minimum and 'winter' tyre 4 mm?",
        a: "Any tyre on a car must have at least 1.5 mm across the principal tread pattern for a WOF. The 4 mm figure only applies to tyres marketed as winter tyres — it's the minimum for them to still be considered winter tyres in NZ.",
      },
      {
        q: "Can I combine an alignment or rotation with my tyre quote?",
        a: "Yes — pick multiple services in the same request. Shops will quote on the combined job.",
      },
      {
        q: "Do shops see each other's Christchurch quotes?",
        a: "No. Each shop quotes privately. Rival shops can't see each other's prices, notes, or your contact details.",
      },
      {
        q: "How soon can I get fitted?",
        a: "It depends on the shop and stock. Each quote lists the earliest service date the shop can offer — you'll see it before you choose.",
      },
    ],
  },
};

export default function CityLandingPage({ citySlug } = {}) {
  const params = useParams();
  const navigate = useNavigate();
  const key = (citySlug || params.city || "").toLowerCase();
  const cfg = CITIES[key];

  if (!cfg) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 text-center">
        <div>
          <p className="text-lg font-medium mb-3">City not found.</p>
          <Button onClick={() => navigate("/")}>Back to home</Button>
        </div>
      </div>
    );
  }

  const canonicalPath = `/tyre-quotes-${cfg.slug}`;
  const canonicalUrl = `https://tyrematch.co.nz${canonicalPath}`;
  const title = `Tyre quotes ${cfg.name} NZ | Private multi-shop quotes | TyreMatch`;

  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: cfg.faq.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  const breadcrumbJsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://tyrematch.co.nz/" },
      { "@type": "ListItem", position: 2, name: `Tyre quotes ${cfg.name}`, item: canonicalUrl },
    ],
  };

  return (
    <div className="min-h-screen bg-background">
      <SEO
        page="cityLanding"
        customTitle={title}
        customDescription={cfg.metaDesc}
        customCanonicalPath={canonicalPath}
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbJsonLd)}</script>
      </Helmet>

      <header className="border-b bg-background/95 backdrop-blur sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <Link to="/" className="font-heading font-bold text-lg text-primary" data-testid="city-home-link">
            TyreMatch
          </Link>
          <nav className="flex items-center gap-4 text-sm">
            <Link to="/how-it-works" className="text-muted-foreground hover:text-primary">How it works</Link>
            <Link to="/tread-depth-nz" className="text-muted-foreground hover:text-primary hidden sm:inline">Tread depth</Link>
            <Button size="sm" onClick={() => navigate("/quote-request")} data-testid="city-cta-quote-btn">
              Get quotes
            </Button>
          </nav>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-10 space-y-10">
        {/* Hero */}
        <section>
          <p className="text-sm text-primary font-semibold uppercase tracking-wider mb-2">
            Tyre quotes · {cfg.name}
          </p>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold leading-tight mb-4">
            Private tyre quotes from local {cfg.name} shops
          </h1>
          <p className="text-lg text-muted-foreground">{cfg.heroLead}</p>
          <div className="mt-5 flex flex-wrap gap-3">
            <Button
              size="lg"
              className="rounded-full"
              onClick={() => navigate("/quote-request")}
              data-testid="city-hero-cta"
            >
              Get {cfg.name} quotes
              <ChevronRight className="ml-2 h-4 w-4" />
            </Button>
            <Link
              to="/how-it-works"
              className="text-primary text-sm self-center underline hover:no-underline"
              data-testid="city-hero-hiw-link"
            >
              See how it works
            </Link>
          </div>
        </section>

        {/* 3-step */}
        <section className="grid sm:grid-cols-3 gap-4">
          {[
            { icon: Search, title: "1. Request", body: `Enter your rego (or tyre size), the service(s) you need, and set a search radius from your ${cfg.name} address.` },
            { icon: Mail, title: "2. Get private quotes", body: `Nearby ${cfg.name} shops send private quotes. Rivals can't see each other's prices.` },
            { icon: CheckCircle2, title: "3. Compare & book", body: "Compare on price, brand, ratings and fitment date. Pay the shop at fitment." },
          ].map((s, i) => (
            <div key={i} className="rounded-xl border p-4 bg-card">
              <s.icon className="h-5 w-5 text-primary mb-2" />
              <h3 className="font-semibold mb-1 text-sm">{s.title}</h3>
              <p className="text-sm text-muted-foreground">{s.body}</p>
            </div>
          ))}
        </section>

        {/* Long-form paragraphs */}
        {cfg.body.map((sec, i) => (
          <section key={i}>
            <h2 className="font-heading text-2xl font-bold mb-3">{sec.h}</h2>
            <p className="text-muted-foreground leading-relaxed">{sec.p}</p>
          </section>
        ))}

        {/* Honesty / marketplace box */}
        <section className="rounded-xl border-l-4 border-primary bg-primary/5 p-4 text-sm space-y-2">
          <p className="flex items-start gap-2">
            <ShieldCheck className="h-4 w-4 mt-0.5 shrink-0 text-primary" />
            <span>
              <strong>Private quotes, always.</strong> Rival shops never see your quotes or your contact details.
            </span>
          </p>
          <p className="flex items-start gap-2">
            <MapPin className="h-4 w-4 mt-0.5 shrink-0 text-primary" />
            <span>
              Quote volume depends on the live shops in your radius on the day — widen the radius if the first batch is thin.
            </span>
          </p>
          <p className="flex items-start gap-2">
            <Wrench className="h-4 w-4 mt-0.5 shrink-0 text-primary" />
            <span>
              You pay the tyre shop directly at fitment. TyreMatch is a marketplace — not the tyre seller or the payment processor for fitment.
            </span>
          </p>
        </section>

        {/* Internal link block */}
        <section className="rounded-xl border p-4 bg-card">
          <h2 className="font-semibold mb-2 text-sm flex items-center gap-2">
            <Info className="h-4 w-4 text-primary" /> Related
          </h2>
          <ul className="text-sm text-muted-foreground space-y-1">
            <li>
              <Link className="text-primary underline hover:no-underline" to="/how-it-works">
                How private multi-shop tyre quotes work
              </Link>
            </li>
            <li>
              <Link className="text-primary underline hover:no-underline" to="/tread-depth-nz">
                Tyre tread depth NZ: 1.5 mm legal vs ~3 mm wet-safe
              </Link>
            </li>
            <li>
              <Link className="text-primary underline hover:no-underline" to="/for-shops">
                Tyre shop in {cfg.name}? Join TyreMatch as a partner
              </Link>
            </li>
          </ul>
        </section>

        {/* FAQ */}
        <section>
          <h2 className="font-heading text-2xl font-bold mb-3">{cfg.name} FAQ</h2>
          <div className="space-y-3">
            {cfg.faq.map((f, i) => (
              <details key={i} className="rounded-lg border px-3 py-2 bg-card">
                <summary className="cursor-pointer font-medium py-1">{f.q}</summary>
                <p className="text-sm text-muted-foreground mt-2">{f.a}</p>
              </details>
            ))}
          </div>
        </section>

        {/* Closing CTA */}
        <section className="rounded-2xl bg-primary text-primary-foreground p-6 text-center">
          <h2 className="font-heading text-2xl font-bold mb-2">Ready for {cfg.name} quotes?</h2>
          <p className="text-primary-foreground/85 mb-4 text-sm">
            One request. Nearby shops. Compare privately.
          </p>
          <Button
            size="lg"
            onClick={() => navigate("/quote-request")}
            className="bg-background text-foreground hover:bg-background/90 rounded-full"
            data-testid="city-closing-cta"
          >
            Get Your Free Quotes Now
            <ChevronRight className="ml-2 h-4 w-4" />
          </Button>
        </section>

        <p className="text-xs text-muted-foreground text-center">
          Not in {cfg.name}? Try our{" "}
          <Link className="underline" to="/how-it-works">how-it-works page</Link>{" "}
          for the full explainer, or start a quote from any NZ address.
        </p>
      </main>

      <footer className="border-t py-6 text-center text-xs text-muted-foreground">
        <div className="max-w-3xl mx-auto px-4">
          TyreMatch is a marketplace connecting NZ customers with local tyre shops. You pay the shop directly.
        </div>
      </footer>
    </div>
  );
}
